import { useField } from 'formik';
import { memo, useCallback } from 'react';

import { Box, Grid, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import { InterviewLanguage, UploadFileInfo } from '@synergetx/puppet-interview-types';

import AudioUploadIconButton from './CreateSurvey/AudioUploadIconButton';
import theme from '@modules/theme';
import AudioPlayer from '@components/AudioPlayer';

// Outlined icons for softer visual appearance (matching PuppetQuestionFields)
const PLAYER_ICONS = {
    PlayIcon: PlayCircleOutlineIcon,
    PauseIcon: PauseCircleOutlineIcon,
    ReplayIcon: ReplayIcon,
};

// Style to apply info (blue) color to player icons
const playerIconStyle = {
    '& .MuiSvgIcon-root': {
        color: theme.palette.info.main,
    },
};

interface Props {
    language: InterviewLanguage;
    audioKeyToSet: 'thatsLikeMe' | 'meToo' | 'whichOneOfUs';
}

const audioKeyToReadableString = {
    thatsLikeMe: 'Thats like me',
    meToo: 'Me too',
    whichOneOfUs: 'Which one of us is more like you',
};

const UploadAudioRow = ({ language, audioKeyToSet }: Props) => {
    const [{ value: generalAudio }, { error: generalAudioError }, { setValue: setGeneralAudio }] =
        useField<string>(`interviewGeneralAudio[${language}].[${audioKeyToSet}]`);

    const onUploadFinish = useCallback(
        (fileInfo: UploadFileInfo) => setGeneralAudio(fileInfo.url),
        [setGeneralAudio],
    );

    return (
        <>
            <Grid
                size={12}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography>
                    {generalAudio?.length > 0 || false
                        ? `"${audioKeyToReadableString[audioKeyToSet]}" audio added`
                        : `Add "${audioKeyToReadableString[audioKeyToSet]}" audio`}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        my: 0.5,
                        gap: 1.5,
                    }}
                >
                    <AudioUploadIconButton
                        path={`GeneralAudio/${language}/${audioKeyToSet}Audio`}
                        onUploadFinish={onUploadFinish}
                        fileUploadedPath={generalAudio || false}
                        generalAudio
                        style={{
                            '&:hover': {
                                backgroundColor: theme.palette.grey[400],
                                borderRadius: 1,
                                cursor: 'pointer',
                            },
                        }}
                    />

                    {generalAudio?.length ? (
                        <Tooltip title="Play audio">
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    ...playerIconStyle,
                                }}
                            >
                                <AudioPlayer
                                    spacing={0}
                                    width="28px"
                                    height="28px"
                                    debug={false}
                                    src={generalAudio || ''}
                                    icons={PLAYER_ICONS}
                                />
                            </Box>
                        </Tooltip>
                    ) : null}
                </Box>
            </Grid>
            {generalAudioError ? (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{
                        mb: 1,
                    }}
                >
                    {generalAudioError}
                </Typography>
            ) : null}
        </>
    );
};

export default memo(UploadAudioRow);
